"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { ArrowRight, ArrowUp, Info, X } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { servicePages } from "@/data/servicePages";
import { sendChat, type ChatTurn } from "@/lib/chat/client";
import {
  openServicesTarget,
  resolveNavigation,
  scrollToSection,
  type ResolvedNavigation,
} from "@/lib/chat/navigation";
import {
  subscribeToOverlay,
  readOverlayOpen,
  readOverlayOpenServer,
} from "@/lib/overlay-state";
import { useBottomBarLift } from "@/lib/bottom-bar-lift";
import { useBodyScrollLock } from "@/lib/use-body-scroll-lock";

/* The Aviva Digital AI assistant — a floating launcher that opens a chat panel.

   It answers questions from the knowledge base in
   supabase/functions/chat/prompt.ts and can navigate the visitor to a page,
   a section or a service. The model only *requests* navigation;
   lib/chat/navigation.ts validates every request against its own allow-list
   before anything happens.

   Ported from the Sallina site — see AI-ASSISTANT-HANDOVER-V2.md for the
   reasoning behind the pieces marked ⚠ below. */

/** Where the in-progress conversation is kept across reloads. */
const CHAT_STORAGE_KEY = "aviva-chat-thread";

/* Reveal the launcher only after a short scroll so it never covers a hero on
   arrival. A plain scroll threshold behaves correctly on every route. */
const REVEAL_AFTER = 160;

/* How long the navigation reply stays readable before the panel closes. */
const NAV_CLOSE_DELAY = 900;

const MOBILE = "(max-width: 640px)";

type Role = "assistant" | "user";

interface Message {
  id: string;
  role: Role;
  text: string;
  /** Human-readable label of where this assistant turn navigated, if any. */
  navLabel?: string;
}

/* Where the assistant asked to go, carried out once the panel has closed. */
type PendingNav =
  | { kind: "scroll"; id: string }
  | { kind: "services"; target: string };

/* Fired on our own window after we write the thread. `storage` only fires in
   OTHER tabs, so without this the component that just saved would never learn
   its own snapshot changed. */
const THREAD_EVENT = "aviva:chat-thread";

/* ⚠ THE CONSENT BANNER IS CLEARED BY LIFTING, NOT BY HIDING. The banner is a
   bar pinned to the bottom; the launcher would land on its buttons. Both the
   launcher and its greeting bubble ride above it on the lift measured by
   lib/bottom-bar-lift.ts (bars mark themselves with data-bottom-bar),
   so a first-time visitor still sees the assistant. */

/* Minimised state — the visitor has tucked the assistant into an edge tab.

   ⚠ localStorage, NOT sessionStorage, unlike the thread. The thread is one
   conversation and should not outlive the visit; this is a PREFERENCE about how
   intrusive the site is allowed to be, and asking someone to dismiss the same
   widget on every visit is the nagging we are trying to remove.

   Same useSyncExternalStore shape as the thread: a `useState` initializer
   would hydration-mismatch on a static export. */
const MINIMISED_KEY = "aviva-chat-minimised";
const MINIMISED_EVENT = "aviva:chat-minimised";

function subscribeToMinimised(onChange: () => void) {
  window.addEventListener(MINIMISED_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(MINIMISED_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

function readMinimised(): boolean {
  try {
    return localStorage.getItem(MINIMISED_KEY) === "1";
  } catch {
    /* Blocked storage — behave as if they never minimised it. */
    return false;
  }
}

function setMinimised(next: boolean) {
  try {
    if (next) localStorage.setItem(MINIMISED_KEY, "1");
    else localStorage.removeItem(MINIMISED_KEY);
    /* Same-tab writes don't fire `storage`; tell our own subscriber. */
    window.dispatchEvent(new Event(MINIMISED_EVENT));
  } catch {
    /* Storage blocked — the toggle still works for this page view. */
  }
}

/* --- the sessionStorage-backed store behind useSyncExternalStore ----------- */

function subscribeToThread(onChange: () => void) {
  window.addEventListener(THREAD_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(THREAD_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/* ⚠ getSnapshot MUST return a referentially stable value between changes, or
   React re-renders forever. JSON.parse allocates a fresh array every call, so
   we cache by the raw string and hand back the same array until it differs. */
let threadCacheRaw: string | null = null;
let threadCacheValue: Message[] | null = null;

function readStoredThread(): Message[] | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
  } catch {
    /* Private mode or blocked storage — behave as if nothing was saved. */
    return null;
  }
  if (raw === threadCacheRaw) return threadCacheValue;
  threadCacheRaw = raw;
  threadCacheValue = null;
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        threadCacheValue = parsed as Message[];
      }
    } catch {
      /* Corrupt entry — start fresh rather than throwing during render. */
    }
  }
  return threadCacheValue;
}

/* `inert` keeps a hidden subtree out of the tab order and away from assistive
   tech. React 18 has no `inert` prop (it drops a boolean one with a warning),
   so it is set on the element directly. */
function useInert(ref: RefObject<HTMLElement | null>, on: boolean) {
  useEffect(() => {
    if (ref.current) ref.current.inert = on;
  }, [ref, on]);
}

/* The Aviva mark — the four-point star from the logo, which doubles as the
   universal "AI" sparkle. Inlined so the flat version inherits currentColor. */
function Sparkle({ className, gradient = false }: { className?: string; gradient?: boolean }) {
  // useId returns ":r1:"; colons are not safe inside url(#…)
  const id = `chat-sparkle-${useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      {gradient && (
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#E040A0" />
            <stop offset="0.55" stopColor="#FFB76C" />
            <stop offset="1" stopColor="#9B59F5" />
          </linearGradient>
        </defs>
      )}
      <path
        fill={gradient ? `url(#${id})` : "currentColor"}
        d="M12 0C12 6.627 17.373 12 24 12C17.373 12 12 17.373 12 24C12 17.373 6.627 12 0 12C6.627 12 12 6.627 12 0Z"
      />
    </svg>
  );
}

/* The site's warm CTA gradient (ButtonPrimary) — fills, never outlines. */
const WARM = "bg-[linear-gradient(135deg,#E040A0_0%,#FFB76C_100%)]";
/* User bubbles: the same warm gradient under the 20% black ButtonPrimary lays
   over it, so white text keeps its contrast at the orange end. */
const USER_BUBBLE =
  "bg-[linear-gradient(rgba(0,0,0,0.2),rgba(0,0,0,0.2)),linear-gradient(135deg,#E040A0_0%,#FFB76C_100%)]";
const EASE = "ease-[cubic-bezier(0.22,1,0.36,1)]";

export default function Chatbot() {
  const { t, locale } = useLang();
  const c = t.chatbot;
  const router = useRouter();
  const pathname = usePathname();

  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  /* The restored thread, read through useSyncExternalStore.

     ⚠ WHY useSyncExternalStore AND NOT AN EFFECT. This site is a static export,
     so the first client render must match the pre-rendered HTML exactly or
     React reports a hydration mismatch — which rules out reading storage in
     useState's initializer. getServerSnapshot supplies the value used while
     hydrating, getSnapshot the real client value, and React swaps between them
     in one commit. Do not "simplify" this back into an effect. */
  const restored = useSyncExternalStore(
    subscribeToThread,
    readStoredThread,
    () => null, // server snapshot: nothing restored, matches the static HTML
  );

  /* How far the launcher must lift to clear the cookie consent banner. The
     hook lifts by the full intrusion; we give back a few px so the launcher
     still reads as belonging to the corner. */
  const barLift = useBottomBarLift(8);
  /* The greeting bubble above the launcher. Dismissed for the session once the
     visitor closes it or opens the chat - an invitation that keeps reappearing
     is nagging. */
  const [bubbleDismissed, setBubbleDismissed] = useState(false);
  /* Tucked into the edge tab. Server snapshot is `false` so the pre-rendered
     HTML always contains the full launcher. */
  const minimised = useSyncExternalStore(
    subscribeToMinimised,
    readMinimised,
    () => false,
  );

  /* Whether a full-screen overlay (the mobile menu) owns the viewport. This
     widget floats above the header, so without this the launcher would hover
     over the open menu. Hides the whole widget, panel included. */
  const overlayOpen = useSyncExternalStore(
    subscribeToOverlay,
    readOverlayOpen,
    readOverlayOpenServer,
  );

  const [ownMessages, setOwnMessages] = useState<Message[] | null>(null);

  /* The intro greeting is derived, not stored — it must follow the active
     locale. Memoized because the fallback allocates a fresh array: without
     this the auto-scroll effect below would re-run on every render. */
  const messages = useMemo(
    () =>
      ownMessages ??
      restored ?? [{ id: "intro", role: "assistant", text: c.msg } as Message],
    [ownMessages, restored, c.msg],
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /* Where the assistant asked to go, carried out once the panel closes. */
  const pendingNav = useRef<PendingNav | null>(null);

  useInert(wrapperRef, overlayOpen);
  useInert(panelRef, !open);

  /* Message ids are derived from the thread's own length: `send` appends at
     most two messages per call, so a length-based id cannot collide, including
     with ids read back from storage. */
  const nextId = (role: Role, offset: number) =>
    `${role}-${messages.length + offset}`;

  /* "Active chat" = the visitor has asked at least one question this session. */
  const hasActivity = messages.some((m) => m.role === "user");

  /* Commit a new thread to state AND to storage in one place, so the two can
     never disagree. */
  const commit = useCallback((next: Message[]) => {
    setOwnMessages(next);
    try {
      sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(next));
      /* Same-tab writes don't fire `storage`; tell our own subscribers. */
      window.dispatchEvent(new Event(THREAD_EVENT));
    } catch {
      /* Storage full or blocked — the chat still works in memory. */
    }
  }, []);

  /* Auto-scroll to the latest message (or the typing indicator). */
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, thinking]);

  /* Focus the input when the panel opens — but not on mobile, where focusing
     immediately summons the keyboard on top of the opening animation. */
  useEffect(() => {
    if (!open) return;
    if (window.matchMedia(MOBILE).matches) return;
    inputRef.current?.focus();
  }, [open]);

  /* On mobile the panel is a full-screen layer, so lock the body while it is
     open — otherwise the page behind scrolls under it and iOS rubber-bands the
     whole document. */
  useBodyScrollLock(open, MOBILE);

  /* Keyboard fit. The on-screen keyboard shrinks the *visual* viewport but
     leaves the layout viewport (and therefore 100dvh) at full height, so the
     mobile panel is sized AND placed from `window.visualViewport`.

     ⚠ TWO NUMBERS, NOT ONE. `height` ← vv.height makes the compose bar clear
     the keyboard; `top` ← vv.offsetTop is non-negotiable, because iOS also
     scrolls the visual viewport down inside the layout viewport and a
     `position: fixed` panel at top:0 would lose its header off the top edge. */
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!open || !vv) return;
    if (!window.matchMedia(MOBILE).matches) return;

    const root = document.documentElement;
    let frame = 0;
    const apply = () => {
      /* iOS fires resize/scroll many times as the keyboard slides. Coalesce
         into one write per frame so the panel resizes smoothly. */
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        root.style.setProperty("--chat-vvh", `${vv.height}px`);
        root.style.setProperty("--chat-vvt", `${vv.offsetTop}px`);
        /* Shrinking the list would otherwise strand the newest message above
           the fold. Jump (not smooth-scroll) so it tracks the keyboard. */
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
      });
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
      root.style.removeProperty("--chat-vvh");
      root.style.removeProperty("--chat-vvt");
    };
  }, [open]);

  /* Close on Escape. */
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setShowDisclaimer(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  /* Reveal the launcher after a short scroll. Stays revealed after that. */
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY >= REVEAL_AFTER) setRevealed(true);
    };
    onScroll(); /* handle reloads that restore a scrolled position */
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  /* Carry out the requested scroll / service tab once the panel has CLOSED —
     same page or cross-page alike.

     ⚠ NOT WHILE THE PANEL IS OPEN. On mobile useBodyScrollLock pins the body
     with position:fixed, so a scroll issued then moves nothing, and the lock's
     cleanup restores the ORIGINAL offset on close. Every navigation closes the
     panel (NAV_CLOSE_DELAY), and React runs all passive cleanups before any
     passive setup in a commit, so the lock is already released here. If the
     target isn't there yet (a lazy section, a route still loading), one
     retry. */
  useEffect(() => {
    if (open || !pendingNav.current) return;
    const target = pendingNav.current;
    pendingNav.current = null;
    const run = () =>
      target.kind === "scroll"
        ? scrollToSection(target.id)
        : openServicesTarget(target.target);
    let timer = 0;
    const frame = requestAnimationFrame(() => {
      if (!run()) timer = window.setTimeout(run, 400);
    });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [open]);

  /* A short label for where the assistant is taking the visitor, e.g.
     "FAQ · Services & pricing" or "Meta реклами".

     ⚠ In Bulgarian it is interpolated into „Отвеждам ви към „{dest}““, and the
     quotes are not decoration: labels mix noun phrases and prepositional
     phrases ("За нас"), and only a quoted title reads right after the verb.
     Keep the quotes in any new interpolated BG string. */
  const navLabelFor = useCallback(
    (page: string, r: ResolvedNavigation): string => {
      const pages: Record<string, string> = c.pages;
      const sections: Record<string, string> = c.sections;
      if (r.landingSlug) {
        const landing = servicePages.find((p) => p.slug === r.landingSlug);
        if (landing) return landing.name[locale];
      }
      if (r.servicesTarget) {
        const label =
          t.categoriesList.find((cat) => cat.id === r.servicesTarget)?.label ??
          t.servicesList.find((s) => s.id === r.servicesTarget)?.title;
        return label ? `${pages.services} · ${label}` : pages.services;
      }
      const pageName = pages[page] ?? page;
      return r.section && sections[r.section]
        ? `${pageName} · ${sections[r.section]}`
        : pageName;
    },
    [c, t, locale],
  );

  /* Execute an assistant navigate request (validated against the allow-list).
     Returns a label for the chip under the message, or undefined when the
     request was rejected — a hallucinated page is ignored silently. */
  const runNavigation = useCallback(
    (nav: { page: string; section?: string } | null): string | undefined => {
      if (!nav) return undefined;
      const resolved = resolveNavigation(nav, pathname);
      if (!resolved) return undefined;

      /* Never scroll here — the close effect above does it. */
      pendingNav.current = resolved.servicesTarget
        ? { kind: "services", target: resolved.servicesTarget }
        : resolved.section
          ? { kind: "scroll", id: resolved.section }
          : null;
      if (!resolved.samePage) router.push(resolved.path);
      return navLabelFor(nav.page, resolved);
    },
    [pathname, router, navLabelFor],
  );

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;

    const userMsg: Message = { id: nextId("user", 0), role: "user", text: trimmed };
    /* `messages` is the resolved thread for this render, so the appended array
       is built explicitly — commit() writes the same value to state and to
       storage. Two sends cannot race: the `thinking` guard refuses the second. */
    const withUser = [...messages, userMsg];
    commit(withUser);
    setInput("");
    setThinking(true);

    const reply = (text: string, navLabel?: string): Message => ({
      id: nextId("assistant", 1),
      role: "assistant",
      text,
      navLabel,
    });

    /* Build the history the model sees (exclude the static intro greeting). */
    const history: ChatTurn[] = withUser
      .filter((m) => m.id !== "intro")
      .map((m) => ({ role: m.role, content: m.text }));

    try {
      const result = await sendChat(locale, history, pathname);
      const navLabel = runNavigation(result.navigate);
      /* Models often emit a tool call with NO accompanying text. Rather than
         navigate silently, speak a localized confirmation that already names
         the destination — and skip the chip so it isn't said twice. */
      const synthesized = !result.text && !!navLabel;
      const replyText = result.text
        ? result.text
        : navLabel
          ? c.navConfirm.replace("{dest}", navLabel)
          : c.error;
      setThinking(false);
      commit([...withUser, reply(replyText, synthesized ? undefined : navLabel)]);
      /* The visitor asked to be taken somewhere, so get out of the way: on a
         phone the panel covers the whole viewport. The short beat lets the
         reply paint first; the thread persists, so reopening shows it. */
      if (navLabel) {
        setTimeout(() => {
          setOpen(false);
          setShowDisclaimer(false);
        }, NAV_CLOSE_DELAY);
      }
    } catch {
      setThinking(false);
      commit([...withUser, reply(c.error)]);
    }
  }

  /* ⚠ ENTER INSERTS A NEWLINE — IT DOES NOT SEND. Sending is the arrow button
     beside the field, which is always visible. With Enter-to-send the textarea
     could not be broken into paragraphs, and a half-written message went out.
     If a keyboard send is ever wanted, gate it on a modifier. */

  /* Only invite someone who has not started talking, on a launcher they can
     actually see, and never over the top of the open panel. */
  const showBubble =
    revealed && !open && !hasActivity && !bubbleDismissed && !minimised;

  return (
    /* ⚠ pointer-events-none on the wrapper is LOAD-BEARING. The flex box wraps
       the 380px panel even while it is closed, so without it the widget
       silently eats clicks across the whole bottom-right region. Only the
       launcher and the open panel re-enable pointer events. If something near
       the bottom-right becomes unclickable, suspect this first.

       Hidden rather than unmounted while the mobile menu is up, so an open
       conversation survives it. ⚠ `display` is toggled through a UTILITY, not
       the `hidden` attribute, which the `flex` utility would out-specify. */
    <div
      ref={wrapperRef}
      className={`pointer-events-none fixed right-4 bottom-4 z-[70] flex-col items-end gap-3 transition-[padding] duration-300 ${EASE} lg:right-6 lg:bottom-6 ${
        overlayOpen ? "hidden" : "flex"
      }`}
      /* Padding, not `bottom`: lifts the stack's content clear of the consent
         banner while the wrapper keeps its corner. */
      style={barLift ? { paddingBottom: `${barLift}px` } : undefined}
    >
      {/* ── Panel ──────────────────────────────────────────────────────── */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={c.name}
        className={
          "flex origin-bottom-right flex-col overflow-hidden rounded-[20px] border border-white/10 " +
          "bg-[#111118] text-white shadow-[0_8px_40px_rgba(0,0,0,0.55)] transition-[opacity,transform] duration-300 " +
          EASE +
          " " +
          /* Desktop: a floating card. */
          "h-[560px] max-h-[calc(100dvh-7rem)] w-[380px] max-w-[calc(100vw-2rem)] " +
          /* Mobile: a full-screen layer, placed by --chat-vvt and sized by
             --chat-vvh so the whole panel — head included — sits inside the
             visual viewport once the keyboard is up. */
          "max-sm:fixed max-sm:inset-x-0 max-sm:bottom-auto max-sm:top-[var(--chat-vvt,0px)] " +
          "max-sm:h-[var(--chat-vvh,100dvh)] max-sm:max-h-none max-sm:w-full max-sm:max-w-full " +
          "max-sm:rounded-none max-sm:border-0 " +
          (open
            ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-3 scale-95 opacity-0")
        }
      >
        {/* Head */}
        <div className="relative flex shrink-0 items-center gap-3 border-b border-white/[0.07] px-4 pb-3.5 pt-[max(0.875rem,env(safe-area-inset-top))] sm:pt-3.5">
          <span
            className={`relative grid size-9 shrink-0 place-items-center overflow-hidden rounded-full ${WARM}`}
          >
            <span className="absolute inset-0 bg-black/20" />
            <Sparkle
              className={`relative size-[18px] text-white ${thinking ? "motion-safe:animate-spin" : ""}`}
            />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <b className="text-[15px] font-semibold text-white">{c.name}</b>
            <span className="flex items-center gap-1.5 text-[12px] text-white/50">
              <span aria-hidden="true" className="size-1.5 rounded-full bg-emerald-400" />
              {c.status}
            </span>
          </div>

          <button
            type="button"
            aria-label={c.disclaimerLabel}
            aria-expanded={showDisclaimer}
            onClick={() => setShowDisclaimer((v) => !v)}
            className={
              "grid size-11 cursor-pointer place-items-center rounded-full transition-colors " +
              (showDisclaimer
                ? "bg-white/10 text-accent-orange"
                : "text-white/50 hover:bg-white/[0.06] hover:text-white")
            }
          >
            <Info className="size-[17px]" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            aria-label={c.close}
            onClick={() => {
              setOpen(false);
              setShowDisclaimer(false);
            }}
            className="grid size-11 cursor-pointer place-items-center rounded-full text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white"
          >
            <X className="size-[18px]" strokeWidth={1.75} />
          </button>

          {/* AI-transparency notice. Not decorative — it is the disclosure that
              the answers are machine-generated. */}
          {showDisclaimer && (
            <div
              role="status"
              className="absolute inset-x-3 top-[calc(100%+0.5rem)] z-10 flex gap-2 rounded-[14px] border border-white/10 bg-[#16161F] p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)] motion-safe:animate-[chat-pop_200ms_both]"
            >
              <Info className="mt-px size-3.5 shrink-0 text-accent-orange" strokeWidth={2} />
              <p className="text-[12px] leading-relaxed text-white/70">{c.disclaimer}</p>
            </div>
          )}
        </div>

        {/* Message list — the only flexible row, so it absorbs the keyboard's
            height change. Owns its scroll, with overscroll contained so a
            bounce at the end doesn't chain to the document behind. */}
        <div
          ref={listRef}
          className="preview-scroll flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto overscroll-contain px-4 pb-1 pt-3"
        >
          {messages.map((m) => (
            <div key={m.id} className="flex flex-col gap-1.5">
              {m.text && (
                <div
                  className={
                    "max-w-[85%] whitespace-pre-wrap rounded-[16px] px-3.5 py-2.5 text-[14px] leading-relaxed [overflow-wrap:anywhere] " +
                    (m.role === "user"
                      ? `self-end rounded-br-md text-white ${USER_BUBBLE}`
                      : "self-start rounded-bl-md border border-white/[0.06] bg-white/[0.05] text-white/90")
                  }
                >
                  {m.text}
                </div>
              )}
              {m.navLabel && (
                <span className="inline-flex items-center gap-1.5 self-start text-[12px] font-medium text-accent-orange">
                  <ArrowRight className="size-3.5" strokeWidth={2} />
                  {c.navTo.replace("{dest}", m.navLabel)}
                </span>
              )}
            </div>
          ))}
          {thinking && (
            <div
              aria-label={c.thinking}
              role="status"
              className="flex items-center gap-1.5 self-start rounded-[16px] rounded-bl-md border border-white/[0.06] bg-white/[0.05] px-3.5 py-3.5"
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="size-1.5 rounded-full bg-white/60 motion-safe:animate-[chat-typing_1.3s_infinite_both]"
                  style={{ animationDelay: `${i * 180}ms` }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Suggested questions */}
        <div className="flex shrink-0 flex-wrap gap-2 px-4 pb-1 pt-2.5">
          {c.chips.map((chip) => (
            <button
              key={chip}
              type="button"
              disabled={thinking}
              onClick={() => send(chip)}
              className="min-h-[44px] cursor-pointer rounded-pill border border-white/10 px-3.5 py-1.5 text-[13px] text-white/70 transition-colors hover:border-accent-pink/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
            >
              {chip}
            </button>
          ))}
        </div>

        {/* Compose */}
        <div className="mx-3 mb-[max(0.875rem,env(safe-area-inset-bottom))] mt-2.5 flex shrink-0 items-end gap-2 rounded-[16px] border border-white/10 bg-white/[0.04] py-2 pl-3.5 pr-2 transition-colors focus-within:border-accent-pink/60">
          {/* ⚠ text-base (16px) is a hard floor, not a style choice. iOS Safari
              force-zooms the page whenever a focused input's font-size is below
              16px. Do not drop it to match the bubbles — and a 16px override in
              a mobile-only variant would NOT save it (both single-class rules;
              the later one wins the cascade regardless of viewport). */}
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            placeholder={c.placeholder}
            onChange={(e) => setInput(e.target.value)}
            aria-label={c.placeholder}
            className="max-h-24 flex-1 resize-none bg-transparent py-1.5 text-base leading-relaxed text-white outline-none placeholder:text-white/35"
          />
          <button
            type="button"
            aria-label={c.send}
            disabled={!input.trim() || thinking}
            onClick={() => send(input)}
            className={`relative grid size-11 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-full text-white transition-[transform,opacity] ${EASE} enabled:hover:scale-105 disabled:cursor-not-allowed disabled:opacity-40 sm:size-9 ${WARM}`}
          >
            <span className="absolute inset-0 bg-black/20" />
            <ArrowUp className="relative size-[18px]" strokeWidth={2.25} />
          </button>
        </div>
      </div>

      {/* ── Greeting bubble ────────────────────────────────────────────── */}
      {/* Says what the assistant is for before the visitor has to guess. Sits
          in the same flex column as the launcher, so it stacks above it and
          inherits the consent-bar lift. */}
      {showBubble && (
        <div className="pointer-events-auto relative mb-0.5 max-w-[16rem] motion-safe:animate-[chat-pop_.35s_cubic-bezier(0.22,1,0.36,1)_both]">
          <p className="rounded-[16px] border border-white/10 bg-[#16161F] px-3.5 py-2.5 pr-8 text-[14px] leading-snug text-white/90 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
            {c.bubble}
          </p>
          {/* The balloon's tail, pointing down at the launcher: a rotated square
              whose bottom/right borders form the V. Centred on the 56px
              launcher below (28px from the right edge minus half its 14px).
              Both it and the bubble are opaque, so it covers the bubble's
              bottom border where they join. */}
          <span
            aria-hidden="true"
            className="absolute -bottom-[7px] right-[21px] size-3.5 rotate-45 border-b border-r border-white/10 bg-[#16161F]"
          />
          <button
            type="button"
            aria-label={c.close}
            onClick={() => setBubbleDismissed(true)}
            className="absolute right-1.5 top-1.5 grid size-6 cursor-pointer place-items-center rounded-full text-white/45 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="size-3.5" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* ── Minimised edge tab ─────────────────────────────────────────── */}
      {/* What "minimised" leaves behind: a small "AVI" pill with the sparkle,
          flush with the viewport edge — a quiet label, not a second launcher.
          The 44px touch target comes from py-*, which pads the hit area
          without inflating what is drawn. */}
      {minimised && !open && revealed && (
        <button
          type="button"
          /* A distinct name from the launcher's, so two buttons never read
             identically in a screen reader's element list. */
          aria-label={c.restore}
          title={c.restore}
          onClick={() => setMinimised(false)}
          /* -mr-* MATCHES the wrapper's right inset (right-4 / lg:right-6) so
             the pill's rounded edge sits flush against the viewport edge. */
          className="group/tab pointer-events-auto -mr-4 flex cursor-pointer items-center py-2.5 lg:-mr-6"
        >
          <span className="flex items-center gap-1.5 rounded-l-pill border border-r-0 border-white/10 bg-[#16161F]/95 py-1.5 pl-2.5 pr-3.5 text-[13px] font-semibold leading-none tracking-wide text-white/70 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl transition-[padding,color] group-hover/tab:pr-5 group-hover/tab:text-white">
            <Sparkle gradient className="size-4 shrink-0" />
            AVI
          </span>
        </button>
      )}

      {/* ── Launcher ───────────────────────────────────────────────────── */}
      {/* Not rendered at all while minimised — hiding it with opacity loses to
          the later opacity-100 utility on stylesheet order. It stays mounted
          whenever the panel is open so closing still works. */}
      {(!minimised || open) && (
        <div
          className={
            `group/launcher pointer-events-auto relative transition-[opacity,transform] duration-500 ${EASE} ` +
            (open ? "max-sm:pointer-events-none max-sm:scale-75 max-sm:opacity-0 " : "") +
            (revealed || open
              ? "translate-y-0 scale-100 opacity-100"
              : "pointer-events-none translate-y-8 scale-75 opacity-0")
          }
        >
          <button
            type="button"
            aria-label={open ? c.close : c.toggleLabel}
            aria-expanded={open}
            onClick={() => {
              setOpen(!open);
              setBubbleDismissed(true);
            }}
            /* ⚠ EMIT EXACTLY ONE size-* CLASS. Tailwind resolves conflicting
               utilities by stylesheet order, not by their order in the class
               attribute, so "size-14 ... size-10" does not reliably shrink
               anything. Pick the size in the ternary instead. */
            className={
              `relative grid cursor-pointer place-items-center rounded-full transition-transform duration-300 ${EASE} hover:scale-[1.08] active:scale-95 ` +
              (open ? "size-11" : "size-14")
            }
          >
            {/* Same language as the collapsed cookie button: a soft warm glow
                behind a dark glass disc. */}
            {!open && (
              <span
                aria-hidden="true"
                className={`absolute -inset-1.5 rounded-full opacity-50 blur-md motion-safe:animate-[chat-glow_2.4s_ease-in-out_infinite] ${WARM}`}
              />
            )}
            <span
              aria-hidden="true"
              className={
                "absolute inset-0 overflow-hidden rounded-full " +
                (open
                  ? `${WARM} shadow-[0_8px_24px_rgba(0,0,0,0.45)]`
                  : "border border-white/15 bg-[#111118]/95 backdrop-blur-xl")
              }
            >
              {open && <span className="absolute inset-0 bg-black/20" />}
            </span>
            {open ? (
              <X className="relative size-[18px] text-white" strokeWidth={2} />
            ) : (
              <Sparkle
                gradient
                className="relative size-6 drop-shadow-[0_0_6px_rgba(255,183,108,0.55)]"
              />
            )}
            {/* A conversation is under way behind the closed launcher */}
            {!open && hasActivity && (
              <span
                aria-hidden="true"
                className="absolute bottom-0.5 right-0.5 size-3 rounded-full border-2 border-[#111118] bg-accent-pink"
              />
            )}
          </button>

          {/* Minimise. Only on the CLOSED launcher: while the panel is open the
              launcher itself closes the chat. Small and low-contrast — an
              escape hatch, not a call to action. Appears on hover/focus with a
              pointer; always visible on touch, where there is no hover. */}
          {!open && !minimised && (
            <button
              type="button"
              aria-label={c.minimise}
              title={c.minimise}
              onClick={() => {
                setMinimised(true);
                setBubbleDismissed(true);
              }}
              className="absolute -right-1.5 -top-1.5 grid size-6 cursor-pointer place-items-center rounded-full border border-white/15 bg-[#16161F] text-white/55 opacity-0 shadow-[0_4px_12px_rgba(0,0,0,0.5)] transition-opacity hover:text-white focus-visible:opacity-100 group-hover/launcher:opacity-100 max-sm:opacity-100"
            >
              <X className="size-3" strokeWidth={2.25} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
